import pdfMake from 'pdfmake'
import { useEffect, useState } from 'react'
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import {
  ArrowRepeat,
  CaretDown,
  CaretUp,
  PencilFill,
  PlusCircleFill,
  PrinterFill,
  Search,
  TrashFill,
} from 'react-bootstrap-icons'
import ReactPaginate from 'react-paginate'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { CustomButton } from '../../components/CustomButton'
import { CustomSpinner } from '../../components/CustomSpinner'
import Navbar from '../../components/navbar/navbar'
import { useOrderTable } from '../../hooks/useOrderTable'
import { usePaginate } from '../../hooks/usePaginate'
import ReclamacoesService from '../../services/database/ReclamacoesService'
import { formatDate } from '../../utils/formatDate'
import { formatPrice } from '../../utils/formatPrice'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'
import { ModalCreate } from './ModalCreate'
import { ModalEdit } from './ModalEdit'

export function Reclamacoes() {
  const [reclamacoes, setReclamacoes] = useState([])
  const [status, setStatus] = useState([])

  const [selectedStatus, setSelectedStatus] = useState('')
  const [cliente, setCliente] = useState('')
  const [selectedClaimCode, setSelectedClaimCode] = useState('')

  const [isRequestLoading, setIsRequestLoading] = useState(true)
  const [isStatusLoading, setStatusLoading] = useState(true)
  const [isSyncLoading, setIsSyncLoading] = useState(false)
  const [isDeleteRequestLoading, setIsDeleteRequestLoading] = useState({})

  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false)
  const [isModalEditOpen, setIsModalEditOpen] = useState(false)

  const {
    sortedArray: sortedReclamacoes,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(reclamacoes, 'codigo')

  const {
    filteredItems: filteredReclamacoes,
    itemsPerPage,
    handlePageClick,
    pageCount,
    setItemsPerPage,
  } = usePaginate(sortedReclamacoes, orderType)

  async function loadStatus() {
    try {
      setStatusLoading(true)

      const data = await ReclamacoesService.getStatus()
      setStatus(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível carregar o status!')
    } finally {
      setStatusLoading(false)
    }
  }

  async function loadReclamacoes(status, cliente) {
    try {
      setIsRequestLoading(true)
      const data = await ReclamacoesService.getAll({ status, cliente })

      setReclamacoes(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível carregar as reclamações!')
    } finally {
      setIsRequestLoading(false)
    }
  }

  async function handleDelete(code, index) {
    try {
      setIsDeleteRequestLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      await ReclamacoesService.delete(code)
      loadReclamacoes()
      toast.success('Reclamação deletada com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível deletar a reclamação!')
    } finally {
      setIsDeleteRequestLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function handleAskDelete(code, index) {
    Swal.fire({
      title: `Deseja deletar a reclamação #${code}?`,
      showDenyButton: true,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      denyButtonText: `Deletar`,
      showConfirmButton: false,
    }).then((result) => {
      if (result.isDenied) {
        handleDelete(code, index)
      }
    })
  }

  async function printReportOperationErrors(reclamacao) {
    const dateFormatted = formatDate(new Date(), 'dd/MM/yyyy HH:mm:ss')

    const docDefinition = {
      header: function (currentPage, pageCount) {
        return [
          {
            columns: [
              {
                text: `${dateFormatted}`,
                style: 'date',
              },
              {
                text: `Página ${currentPage.toString()} de ${pageCount}`,
                style: 'page',
              },
            ],
          },
        ]
      },
      content: [
        { text: 'Relatório de Erros de Operação', style: 'title' },
        {
          text: `Agente: ${reclamacao?.agente || 'Não Identificado'}`,
          style: 'clientName',
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', '*', '*', '*'],
            body: [
              [
                { text: 'Código', style: 'tableRow' },
                { text: 'Ocorrência', style: 'tableRow' },
                { text: 'Pedido', style: 'tableRow' },
                { text: 'Conta', style: 'tableRow' },
                { text: 'Data', style: 'tableRow' },
              ],
              [
                {
                  text: reclamacao.codigo,
                  style: 'tableRow',
                },
                {
                  text: reclamacao.motivo_reclamacao,
                  style: 'tableRow',
                },
                {
                  text: reclamacao.numero_pedido,
                  style: 'tableRow',
                },
                {
                  text: reclamacao.id_loja,
                  style: 'tableRow',
                },
                {
                  text: formatDate(reclamacao.data_entrada),
                  style: 'tableRow',
                },
              ],
            ],
          },
          margin: [0, 20],
        },
        { text: 'Débitos', style: 'subtitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', '*', '*'],
            body: [
              [
                { text: 'Tx. Envio', style: 'tableRow' },
                { text: 'Tx. Retorno', style: 'tableRow' },
                { text: 'Tx Débito ML', style: 'tableRow' },
                { text: 'Total', style: 'tableRow' },
              ],
              [
                {
                  text: formatPrice(reclamacao.taxa_envio),
                  style: 'tableRow',
                },
                {
                  text: formatPrice(reclamacao.taxa_retorno),
                  style: 'tableRow',
                },
                {
                  text: formatPrice(reclamacao.debito_ml),
                  style: 'tableRow',
                },
                {
                  text: formatPrice(
                    reclamacao.taxa_envio +
                      reclamacao.taxa_retorno +
                      reclamacao.debito_ml,
                  ),
                  style: 'tableRow',
                },
              ],
            ],
          },
          margin: [0, 10],
        },
        {
          text: `Problema Ocasionado: ${reclamacao?.problema_ocasionado || 'Não Identificado'}`,
          style: 'trouble',
        },
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 515,
              y2: 0,
              lineWidth: 1,
            },
          ],
          margin: [0, 40, 0, 0],
        },
        {
          text: `Assinatura: ${reclamacao?.agente || 'Não Identificado'}`,
          style: 'signature',
          margin: [0, 5],
        },
      ],
      styles: {
        date: { bold: true, alignment: 'left', fontSize: 10, margin: 10 },
        page: {
          bold: true,
          alignment: 'right',
          fontSize: 10,
          margin: 10,
        },
        title: {
          fontSize: 14,
          bold: true,
          alignment: 'center',
        },
        subtitle: {
          fontSize: 12,
          bold: true,
          alignment: 'center',
        },
        clientName: {
          fontSize: 10,
          alignment: 'center',
        },
        tableRow: {
          fontSize: 10,
        },
        trouble: {
          fontSize: 10,
          margin: [0, 20],
        },
        signature: {
          fontSize: 10,
        },
      },
    }

    pdfMake.createPdf(docDefinition).open()
  }

  async function handleSyncReclamacoes() {
    try {
      setIsSyncLoading(true)
      await ReclamacoesService.sync()
      toast.success('Sincronização concluída com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível concluir a sincronização!')
    } finally {
      setIsSyncLoading(false)
    }
  }

  useEffect(() => {
    loadReclamacoes()
    loadStatus()
  }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page">
        <h1>Reclamações</h1>

        {isRequestLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CustomSpinner />
          </div>
        ) : (
          <>
            <Row
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 20,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 20,
                }}
              >
                <Col>
                  <Form.Label>Paginação:</Form.Label>
                  <Form.Select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                    }}
                    style={{
                      width: 100,
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>

                <Col>
                  <Form.Label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    Status:
                    {isStatusLoading && <CustomSpinner size={20} />}
                  </Form.Label>

                  <Form.Select
                    disabled={isStatusLoading}
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e.target.value)
                    }}
                    style={{
                      width: 200,
                    }}
                  >
                    <option value="">
                      {isStatusLoading ? 'Carregando...' : 'TODOS'}
                    </option>

                    {status.map((st) => (
                      <option
                        key={Math.random()}
                        value={st}
                      >
                        {st}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col>
                  <Form.Label>Cliente:</Form.Label>
                  <Form.Control
                    value={cliente}
                    onChange={(e) => {
                      setCliente(e.target.value)
                    }}
                    style={{
                      width: 300,
                    }}
                  />
                </Col>

                <Col
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                  }}
                >
                  <CustomButton
                    title="Pesquisar"
                    onClick={() => loadReclamacoes(selectedStatus, cliente)}
                  >
                    <Search />
                  </CustomButton>
                </Col>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                }}
              >
                <CustomButton
                  color="#05a416"
                  title="Novo"
                  onClick={() => setIsModalCreateOpen(true)}
                >
                  <PlusCircleFill />
                </CustomButton>

                <CustomButton
                  color="#3f0cc5"
                  title="Sincronizar Reclamações"
                  onClick={handleSyncReclamacoes}
                  isLoading={isSyncLoading}
                  isLoadingText="Sincronizando, aguarde..."
                >
                  <ArrowRepeat />
                </CustomButton>
              </div>
            </Row>

            <Table>
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('codigo')}>
                    <span>Código</span>
                    <CaretUp color={check('codigo', 'asc')} />
                    <CaretDown color={check('codigo', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('status')}>
                    <span>Status</span>
                    <CaretUp color={check('status', 'asc')} />
                    <CaretDown color={check('status', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('cliente')}>
                    <span>Cliente</span>
                    <CaretUp color={check('cliente', 'asc')} />
                    <CaretDown color={check('cliente', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('motivo_reclamacao')}>
                    <span>Situação (Motivo)</span>
                    <CaretUp color={check('motivo_reclamacao', 'asc')} />
                    <CaretDown color={check('motivo_reclamacao', 'desc')} />
                  </th>

                  <th>ID Conta</th>
                  <th>Ações</th>
                </tr>
              </thead>

              <tbody>
                {filteredReclamacoes.map((reclamacao, index) => (
                  <tr key={reclamacao.codigo}>
                    <td>{reclamacao.codigo}</td>
                    <td>{reclamacao.status}</td>
                    <td>{reclamacao.cliente}</td>
                    <td>{reclamacao.motivo_reclamacao}</td>
                    <td>{reclamacao.apelido}</td>
                    <td
                      style={{
                        display: 'flex',
                      }}
                    >
                      <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
                        <CustomButton
                          onClick={() => {
                            setSelectedClaimCode(reclamacao.codigo)
                            setIsModalEditOpen(true)
                          }}
                          isEdit
                        >
                          <PencilFill />
                        </CustomButton>
                      </OverlayTrigger>

                      <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
                        <CustomButton
                          isDelete
                          isLoading={isDeleteRequestLoading[index]}
                          onClick={() =>
                            handleAskDelete(reclamacao.codigo, index)
                          }
                        >
                          <TrashFill />
                        </CustomButton>
                      </OverlayTrigger>

                      <OverlayTrigger
                        overlay={
                          <Tooltip>
                            Imprimir Relatório de Erros de Operação
                          </Tooltip>
                        }
                      >
                        <CustomButton
                          color="#1B998B"
                          isLoading={isDeleteRequestLoading[index]}
                          onClick={() => printReportOperationErrors(reclamacao)}
                        >
                          <PrinterFill />
                        </CustomButton>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Próxima"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="Anterior"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              activeClassName="active"
            />
          </>
        )}
      </div>

      {isModalCreateOpen && (
        <ModalCreate
          isOpen={isModalCreateOpen}
          loadReclamacoes={loadReclamacoes}
          onClose={() => setIsModalCreateOpen(false)}
        />
      )}

      {isModalEditOpen && (
        <ModalEdit
          isOpen={isModalEditOpen}
          onClose={() => setIsModalEditOpen(false)}
          code={selectedClaimCode}
          loadReclamacoes={loadReclamacoes}
        />
      )}
    </>
  )
}
